import { useEffect, useRef, useState } from "react";
import { Save, Sparkles, FileText, Upload, X, Bot, Lightbulb, Package, MessageCircle, Zap, PlayCircle, RefreshCw, Send, Loader2 } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { simulateStream } from "@/lib/simulateStream";
const uid = () => Math.random().toString(36).slice(2, 10);

const MAX_FILES = 5;
const MAX_FILE_TEXT_BYTES = 200_000; // 200KB de texto por archivo

export default function SalesAIPage() {
  return (
    <DashboardShell active="sales-ai">
      {({ merchant, setMerchant }) => <SalesAIBody merchant={merchant} setMerchant={setMerchant} />}
    </DashboardShell>
  );
}

function SalesAIBody({ merchant, setMerchant }) {
  const [form, setForm] = useState({
    ai_sales_persona: merchant.ai_sales_persona || "",
    ai_sales_context: merchant.ai_sales_context || "",
    ai_sales_products: merchant.ai_sales_products || "",
    ai_sales_faqs: merchant.ai_sales_faqs || "",
    ai_sales_hooks: merchant.ai_sales_hooks || "",
  });
  const [files, setFiles] = useState(merchant.ai_sales_files || []);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    if (files.length + picked.length > MAX_FILES) {
      toast.error(`Máximo ${MAX_FILES} archivos en total`);
      return;
    }
    const newFiles = [];
    for (const f of picked) {
      const allowed = /\.(txt|csv|md|json)$/i.test(f.name);
      if (!allowed) {
        toast.error(`${f.name}: solo .txt, .csv, .md o .json`);
        continue;
      }
      const text = await f.text();
      if (text.length > MAX_FILE_TEXT_BYTES) {
        toast.error(`${f.name}: excede 200 KB de texto`);
        continue;
      }
      newFiles.push({ name: f.name, mime: f.type || "text/plain", size: f.size, text_content: text });
    }
    if (newFiles.length) {
      setFiles([...files, ...newFiles]);
      toast.success(`${newFiles.length} archivo(s) cargado(s). Recuerda guardar.`);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchants/me", { ...form, ai_sales_files: files });
      setMerchant(data);
      toast.success("Contexto de ventas guardado. La IA ya lo usará en la próxima conversación.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">IA Vendedora</h1>
          <p className="text-neutral-500 mt-1">Enséñale a nuestra IA sobre tu negocio para que venda mejor.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold h-11 px-6" data-testid="sales-ai-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      {/* Step-by-step guide */}
      <div className="rounded-3xl bg-gradient-to-br from-[#FF007A]/8 to-[#0055FF]/8 border border-[#FF007A]/20 p-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-[#FF007A]" />
          <h2 className="font-heading font-extrabold text-lg">Cómo llenar esta página — guía rápida</h2>
        </div>
        <ol className="space-y-2 text-sm text-neutral-700 leading-relaxed">
          <li><strong className="text-black">1. Persona:</strong> ponle nombre y personalidad al asistente. Ej: <em>&ldquo;Sofía, cálida y experta en café de especialidad. Habla como barista amigable.&rdquo;</em></li>
          <li><strong className="text-black">2. Contexto del negocio:</strong> qué vendes, hace cuánto, qué te hace único, valores. Escribe como si le contaras a un empleado nuevo su primer día.</li>
          <li><strong className="text-black">3. Productos estrella:</strong> lista tus 5-10 productos/servicios más vendidos con precios y qué combina bien con qué.</li>
          <li><strong className="text-black">4. Preguntas frecuentes:</strong> las 5-10 dudas más comunes con la respuesta oficial (horarios, si tienes estacionamiento, si aceptan tarjeta, si hay opciones vegetarianas, etc).</li>
          <li><strong className="text-black">5. Ganchos de venta:</strong> frases que uses para convencer clientes (&ldquo;recién horneado hace 20 min&rdquo;, &ldquo;el chef lo prepara al momento&rdquo;, &ldquo;garantía de devolución&rdquo;), promesas, argumentos de cierre.</li>
          <li><strong className="text-black">6. Archivos (opcional):</strong> sube tu menú, catálogo o política interna en formato <code>.txt</code>, <code>.csv</code>, <code>.md</code> o <code>.json</code> (hasta 5 archivos, 200 KB c/u).</li>
        </ol>
        <p className="mt-4 text-xs text-neutral-600">
          💡 <strong>Consejo:</strong> mientras más específico y honesto seas, mejor venderá la IA. Puedes actualizar esta info las veces que quieras — se aplica en la siguiente conversación.
        </p>
      </div>

      {/* Persona */}
      <FieldCard icon={Bot} title="1. Persona del asistente" subtitle="Cómo se llama y cómo habla la IA que representa tu negocio">
        <Input value={form.ai_sales_persona} onChange={(e) => upd("ai_sales_persona", e.target.value)}
          placeholder='Ej: "Sofía, cálida barista experta en café de especialidad. Habla como amiga, nunca formal."'
          className="h-11 rounded-xl" maxLength={200} data-testid="sales-ai-persona" />
        <Counter val={form.ai_sales_persona} max={200} />
      </FieldCard>

      {/* Contexto */}
      <FieldCard icon={Lightbulb} title="2. Contexto del negocio" subtitle="Historia, valores, diferenciadores. Lo que le contarías a un empleado nuevo">
        <Textarea value={form.ai_sales_context} onChange={(e) => upd("ai_sales_context", e.target.value)}
          rows={5} placeholder="Ej: Somos una cafetería familiar abierta desde 2018 en Chapalita. Tostamos nuestro grano de Chiapas cada semana. Lo que nos hace únicos es que servimos el café a la temperatura exacta y regalamos un pan si tu bebida se enfría..."
          className="rounded-xl" maxLength={2000} data-testid="sales-ai-context" />
        <Counter val={form.ai_sales_context} max={2000} />
      </FieldCard>

      {/* Productos */}
      <FieldCard icon={Package} title="3. Productos y servicios estrella" subtitle="Tus 5-10 items más vendidos con precios y combinaciones">
        <Textarea value={form.ai_sales_products} onChange={(e) => upd("ai_sales_products", e.target.value)}
          rows={5}
          placeholder={`Ej:
- Latte Sofía: $65 (combina genial con pan de elote $45)
- Frappé matcha: $85 (nuestro más pedido en tarde)
- Sándwich de pavo: $95 (viene con papas y agua fresca)
- Cheesecake horneado: $75 (con café gratis los martes)`}
          className="rounded-xl font-mono text-sm" maxLength={2000} data-testid="sales-ai-products" />
        <Counter val={form.ai_sales_products} max={2000} />
      </FieldCard>

      {/* FAQs */}
      <FieldCard icon={MessageCircle} title="4. Preguntas frecuentes" subtitle="Lo que más te preguntan y la respuesta oficial">
        <Textarea value={form.ai_sales_faqs} onChange={(e) => upd("ai_sales_faqs", e.target.value)}
          rows={5}
          placeholder={`Ej:
¿Tienen estacionamiento? Sí, gratis en la plaza subterránea, 2 horas.
¿Aceptan tarjeta? Visa, MC, Amex, OXXO y transferencia.
¿Tienen opciones sin gluten? Sí, todo el menú de sándwiches se hace con pan sin gluten sin costo extra.`}
          className="rounded-xl" maxLength={2000} data-testid="sales-ai-faqs" />
        <Counter val={form.ai_sales_faqs} max={2000} />
      </FieldCard>

      {/* Hooks */}
      <FieldCard icon={Zap} title="5. Ganchos de venta y cierre" subtitle="Frases que usas cuando quieres convencer a un cliente indeciso">
        <Textarea value={form.ai_sales_hooks} onChange={(e) => upd("ai_sales_hooks", e.target.value)}
          rows={4}
          placeholder={`Ej:
- Todo se prepara al momento, no tenemos nada del día anterior.
- Si no te enamora el primer sorbo, la casa te invita otro.
- Somos el único local en Guadalajara con café de finca Chiapas Bourbon.
- Los martes 2x1 en frappés después de las 6pm.`}
          className="rounded-xl" maxLength={1500} data-testid="sales-ai-hooks" />
        <Counter val={form.ai_sales_hooks} max={1500} />
      </FieldCard>

      {/* Files */}
      <FieldCard icon={FileText} title="6. Archivos de apoyo (opcional)" subtitle="Menú, catálogo, política de devoluciones… en .txt, .csv, .md o .json">
        <input ref={fileRef} type="file" accept=".txt,.csv,.md,.json" multiple onChange={handleFileUpload} className="hidden" data-testid="sales-ai-file-input" />
        <Button type="button" onClick={() => fileRef.current?.click()} disabled={files.length >= MAX_FILES}
          variant="outline" className="rounded-full h-10 px-4" data-testid="sales-ai-file-btn">
          <Upload className="w-4 h-4" /> Subir archivo ({files.length}/{MAX_FILES})
        </Button>
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((f) => (
              <div key={`${f.name}-${f.size}`} className="flex items-center justify-between rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
                  <span className="text-sm font-semibold truncate">{f.name}</span>
                  <span className="text-xs text-neutral-500 shrink-0">({Math.round((f.text_content?.length || 0) / 1024)} KB)</span>
                </div>
                <button onClick={() => removeFile(i)} className="text-neutral-400 hover:text-red-500" data-testid={`sales-ai-file-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-neutral-500 mt-3 leading-relaxed">
          Los archivos se envían a la IA como texto adicional cuando conversa. Ideal para menús largos o catálogos con muchos SKUs.
        </p>
      </FieldCard>

      <div className="sticky bottom-0 pt-4 pb-2 bg-gradient-to-t from-white via-white to-transparent">
        <Button onClick={save} disabled={saving} className="w-full h-12 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold shadow-lg" data-testid="sales-ai-save-btn-bottom">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar contexto de ventas"}
        </Button>
      </div>

      {/* Simulator: probar IA con la config guardada */}
      <SimulatorSection merchant={merchant} />
    </div>
  );
}

function FieldCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-[#0055FF]/10 text-[#0055FF] grid place-items-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-base font-heading font-extrabold">{title}</Label>
          <p className="text-xs text-neutral-500 mt-0.5 leading-snug">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Counter({ val, max }) {
  const len = (val || "").length;
  const near = len > max * 0.85;
  return <div className={`text-[10px] mt-1 text-right ${near ? "text-amber-600" : "text-neutral-400"}`}>{len}/{max}</div>;
}

// ---------- Simulador de conversación con la IA de este comercio ----------
function newSessionId() {
  return "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function SimulatorSection({ merchant }) {
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const hasSavedContext = Boolean(
    (merchant.ai_sales_persona || "").trim() ||
    (merchant.ai_sales_context || "").trim() ||
    (merchant.ai_sales_products || "").trim()
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, partial]);

  const reset = () => {
    if (abortRef.current) abortRef.current.abort();
    setSessionId(newSessionId());
    setMessages([]);
    setPartial("");
    setStreaming(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: uid(), role: "user", text }]);
    setPartial("");
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    await simulateStream({
      text,
      sessionId,
      reset: false,
      signal: controller.signal,
      onChunk: (t) => setPartial((p) => p + t),
      onDone: ({ full }) => {
        setMessages((m) => [...m, { id: uid(), role: "assistant", text: full }]);
        setPartial("");
        setStreaming(false);
      },
      onError: (err) => {
        const msg = typeof err === "string" ? err : "Error en el simulador";
        toast.error(msg);
        setStreaming(false);
        setPartial("");
      },
    });
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="rounded-3xl border border-[#0055FF]/25 bg-gradient-to-br from-[#0055FF]/5 to-[#FF007A]/5 p-6 space-y-5" data-testid="sales-ai-simulator">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-2xl bg-[#0055FF] text-white grid place-items-center shrink-0">
          <PlayCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">Probar IA con esta configuración</h2>
          <p className="text-sm text-neutral-600 mt-1 leading-snug">
            Habla con tu vendedora IA como lo haría un cliente real. Usa el contexto que <strong>ya guardaste</strong> arriba.
          </p>
        </div>
      </div>

      {!hasSavedContext && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900">
          Aún no has llenado la persona ni el contexto arriba — la IA responderá con configuración por defecto. Para una prueba realista, llena y guarda primero, y regresa aquí.
        </div>
      )}

      {/* Chat */}
      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden flex flex-col">
        <div ref={scrollRef} className="p-4 space-y-3 max-h-96 overflow-y-auto min-h-56" data-testid="sim-messages">
          {messages.length === 0 && !partial && (
            <div className="text-center text-sm text-neutral-400 py-10">
              <Bot className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
              Empieza con un mensaje como lo haría un cliente parado afuera de tu negocio.
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {["Hola, qué venden", "¿Está muy caro?", "Solo estoy viendo, gracias", "¿Tienen algo para llevar rápido?"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-xs bg-neutral-100 hover:bg-neutral-200 px-3 py-1.5 rounded-full text-neutral-700"
                    data-testid={`sim-suggest-${s.slice(0, 10)}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <ChatBubble key={m.id} role={m.role} text={m.text} />
          ))}
          {partial && <ChatBubble role="assistant" text={partial} streaming />}
        </div>

        <div className="border-t border-neutral-200 p-3 bg-neutral-50 flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder='Escribe como cliente: "hola, ¿qué venden?"'
            disabled={streaming}
            className="h-11 rounded-xl bg-white flex-1"
            data-testid="sim-input"
          />
          <Button
            onClick={send}
            disabled={streaming || !input.trim()}
            className="h-11 rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold px-5"
            data-testid="sim-send-btn"
          >
            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="hidden sm:inline">{streaming ? "Pensando" : "Enviar"}</span>
          </Button>
          <Button
            onClick={reset}
            variant="outline"
            className="h-11 rounded-full px-3"
            title="Reiniciar conversación"
            data-testid="sim-reset-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ role, text, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#0055FF] text-white rounded-br-md"
            : "bg-neutral-100 text-neutral-900 rounded-bl-md"
        }`}
        data-testid={isUser ? "sim-user-msg" : "sim-assistant-msg"}
      >
        {text}
        {streaming && <span className="inline-block w-1.5 h-3 ml-1 bg-neutral-400 animate-pulse rounded-sm align-middle" />}
      </div>
    </div>
  );
}
